import { TextProvider } from "../../../context/Text";
import { useTheme } from "../../../context/theme";
import { DISABLED_OPACITY } from "../../../styles/colors/consts";
import { ColorRole, KeyColorRole } from "../../../styles/colors/types";
import {
  typographyBodyLineHeightMap,
  typographyBodySizeMap
} from "../../../styles/typography/typography";
import { Box } from "../../layout";
import { uppercaseStyles } from "../consts";

import type { TagSize, TagStrength } from "./types";

type Props = {
  tone: KeyColorRole;
  strength: TagStrength;
  children: React.ReactNode;
  size: TagSize;
  uppercase?: boolean;
  dataNoTranslate?: boolean;
  disabled?: boolean;
};

export default function Tag({
  children,
  tone,
  strength,
  size,
  uppercase,
  dataNoTranslate,
  disabled = false
}: Props) {
  const { backgroundColor, color, borderColor } = useTagColors(tone, strength);

  return (
    <TextProvider value>
      <Box
        paddingX="12"
        paddingY="small"
        width="max-content"
        height="max-content"
        fontWeight="bold"
        {...typographyBodySizeMap[size]}
        {...typographyBodyLineHeightMap.large}
        opacity={disabled ? DISABLED_OPACITY : "1"}
        backgroundColor={backgroundColor}
        color={color}
        borderColor={borderColor}
        borderStyle={strength === "weakWithOutline" ? "solid" : "none"}
        borderRadius="xxlarge"
        borderWidth="xxxsmall"
        {...(uppercase && uppercaseStyles)}
        dataNoTranslate={dataNoTranslate}
      >
        {children}
      </Box>
    </TextProvider>
  );
}

function useTagColors(tone: KeyColorRole, strength: TagStrength) {
  const theme = useTheme();

  let roles: {
    backgroundColor: ColorRole;
    color: ColorRole;
    borderColor: ColorRole;
  };

  roles = {
    backgroundColor:
      strength === "strong" ? `${tone}-high-surface` : `${tone}-medium-surface`,
    color:
      strength === "strong"
        ? `contentOn-${tone}-high-surface`
        : `${tone}-high-content`,
    borderColor: `${tone}-medium-content`
  };

  return {
    backgroundColor: theme[roles.backgroundColor],
    color: theme[roles.color],
    borderColor: theme[roles.borderColor]
  };
}
